import { Badge, FilterBar, FilterSelect, type Tone } from "@/terabizz"
import { KindBadge } from "@/routes/orders"
import { CHANNEL_LABEL } from "@/lib/orders"
import { EllipsisHorizontal, PencilSquare, Trash } from "@medusajs/icons"
import { Button, Checkbox, Drawer, DropdownMenu, IconButton, Input, Prompt, Text, Textarea, toast } from "@medusajs/ui"
import { useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { TagsDrawer } from "@/routes/products"
import { useSegments } from "@/lib/segments"
import { SortMenu, useColumns, ViewTabs } from "@/components/list-views"
import { CustomerImportDialog } from "@/components/customer-import"
import { downloadExport } from "@/lib/export"
import { DataTable, EmptyState, ErrorState, PageHeader, SaveBar, SearchInput, Section, Status, Thumb, usePage } from "@/components/ui"
import {
  useBulkCustomers,
  useCreateCustomer,
  useCustomerDetail,
  useCustomerList,
  useCustomerSummary,
  useDeleteAddress,
  useDeleteCustomer,
  useSaveAddress,
  useUpdateCustomer,
  type AddressInput,
  type PreviousStore,
} from "@/lib/customers"
import { formatDate, formatMoney, fullName, relativeTime } from "@/lib/format"
import { useCan, useOrderName } from "@/lib/permissions"
import { AddressFields as SharedAddressFields, AddressText, addressProblem } from "@/components/address-fields"
import { errorMessage, sdk } from "@/lib/sdk"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const PAGE = 50

function Subscribed({ state }: { state?: string | null }) {
  const yes = state === "subscribed" || state === "SUBSCRIBED"
  const off = state === "unsubscribed"
  return (
    <span className="tf-pill self-start" style={yes ? { background: "#affebf", color: "#014b40" } : off ? { background: "#fed1d7", color: "#8e1f0b" } : { background: "#e3e3e3", color: "#616161" }}>
      {yes ? "Subscribed" : off ? "Unsubscribed" : "Not subscribed"}
    </span>
  )
}

const SORTS: [string, string][] = [
  ["created_desc", "Customer since (newest)"],
  ["created_asc", "Customer since (oldest)"],
  ["name_asc", "Name A–Z"],
  ["name_desc", "Name Z–A"],
  ["spent_desc", "Amount spent (highest)"],
  ["orders_desc", "Orders (most)"],
  ["last_order_desc", "Last order (newest)"],
  ["prev_spent_desc", "Previous store: amount spent (highest)"],
  ["prev_orders_desc", "Previous store: orders (most)"],
  ["prev_since_asc", "Previous store: customer since (oldest)"],
  ["prev_last_order_desc", "Previous store: last order (newest)"],
]
const MARKETING: [string, string][] = [
  ["subscribed", "Subscribed"],
  ["not_subscribed", "Not subscribed"],
  ["unsubscribed", "Unsubscribed"],
]
const PREVIOUS: [string, string][] = [
  ["yes", "Was a customer"],
  ["with_orders", "Ordered there"],
  ["no", "New here"],
]
const SOURCES: [string, string][] = [
  ["previous_store", "Previous store (recovery)"],
  ["online_store", "Online store"],
  ["added", "Added by staff"],
]
const SOURCE_BADGE: Record<string, { label: string; tone: Tone }> = {
  previous_store: { label: "Previous store", tone: "info" },
  online_store: { label: "Online store", tone: "success" },
  added: { label: "Added by staff", tone: "neutral" },
}
function SourceBadge({ source }: { source?: string }) {
  const b = SOURCE_BADGE[source ?? ""]
  return b ? <Badge tone={b.tone}>{b.label}</Badge> : <span className="text-ui-fg-subtle">—</span>
}
const prevOrders = (p: PreviousStore | null | undefined) => (p ? `${p.orders} order${p.orders === 1 ? "" : "s"}` : "—")

export function CustomersPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const segment = params.get("segment") ?? ""
  const sort = params.get("sort") ?? "created_desc"
  const emailMarketing = params.get("email_marketing") ?? ""
  const smsMarketing = params.get("sms_marketing") ?? ""
  const previous = params.get("previous_store") ?? ""
  const source = params.get("source") ?? ""
  const { data, isLoading, error } = useCustomerList({ q, segment, sort, email_marketing: emailMarketing, sms_marketing: smsMarketing, previous_store: previous, source, limit: PAGE, offset: pageIndex * PAGE })
  const segments = useSegments().data?.segments ?? []
  const navigate = useNavigate()
  const can = useCan()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [tagMode, setTagMode] = useState<"add" | "remove" | null>(null)
  const [importing, setImporting] = useState(false)
  const bulk = useBulkCustomers()
  const setParam = (key: string, v: string, dflt = "") => {
    const next = new URLSearchParams(params)
    v && v !== dflt ? next.set(key, v) : next.delete(key)
    next.delete("page")
    setSelected(new Set())
    setParams(next)
  }
  const setSegment = (v: string) => {
    const next = new URLSearchParams(params)
    v ? next.set("segment", v) : next.delete("segment")
    next.delete("page")
    setSelected(new Set())
    setParams(next)
  }
  const name = (c: { first_name: string | null; last_name: string | null; email: string }) => fullName(c) || c.email
  const cols = useColumns("customers", [
    { key: "sub", label: "Email subscription" },
    { key: "loc", label: "Location" },
    { key: "orders", label: "Orders" },
    { key: "spent", label: "Amount spent" },
    { key: "email", label: "Email", default: false },
    { key: "phone", label: "Phone", default: false },
    { key: "created", label: "Customer since", default: false },
    { key: "sms", label: "SMS subscription", default: false },
    { key: "last", label: "Last order", default: false },
    { key: "prev_orders", label: "Previous store: orders", default: false },
    { key: "prev_spent", label: "Previous store: amount spent", default: false },
    { key: "prev_since", label: "Previous store: customer since", default: false },
    { key: "prev_last", label: "Previous store: last order", default: false },
    { key: "prev_email", label: "Previous store: email subscription", default: false },
  ])

  return (
    <>
      <PageHeader
        title="Customers"
        actions={
          <>
            {can("customers:write") && <Button size="small" variant="secondary" onClick={() => setImporting(true)}>Import</Button>}
            <Button size="small" variant="secondary" onClick={() => downloadExport("/admin/terify/customers/export", { ...(q ? { q } : {}), ...(segment ? { segment } : {}) })}>Export</Button>
            {can("customers:write") && (
              <Button size="small" asChild>
                <Link to="/customers/new">Add customer</Link>
              </Button>
            )}
          </>
        }
      />
      <Section>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <ViewTabs resource="customers" canSave={can("customers:write")} params={params} setParams={(p) => (setSelected(new Set()), setParams(p))} tabs={[{ value: "all", label: "All", params: {} }]} />
          <div className="flex items-center gap-2">
            <SearchInput value={q} onChange={setQ} placeholder="Search customers" />
            <SortMenu value={sort} options={SORTS} onChange={(v) => setParam("sort", v, "created_desc")} />
            {cols.menu}
          </div>
        </div>
        <FilterBar>
          <FilterSelect label="Segment" value={segment} onChange={setSegment} options={segments.map((s) => [s.id, `${s.name} (${s.count ?? 0})`] as [string, string])} />
          <FilterSelect label="Source" value={source} onChange={(v) => setParam("source", v)} options={SOURCES} />
          {!segment && (
            <>
              <FilterSelect label="Email" value={emailMarketing} onChange={(v) => setParam("email_marketing", v)} options={MARKETING} />
              <FilterSelect label="SMS" value={smsMarketing} onChange={(v) => setParam("sms_marketing", v)} options={MARKETING} />
              <FilterSelect label="Previous store" value={previous} onChange={(v) => setParam("previous_store", v)} options={PREVIOUS} />
            </>
          )}
        </FilterBar>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(c) => c.id}
            rows={data?.customers}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(c) => navigate(`/customers/${c.id}`)}
            selected={can("customers:write") ? selected : undefined}
            onSelectedChange={can("customers:write") ? setSelected : undefined}
            bulkActions={
              <>
                <Button size="small" variant="secondary" onClick={() => setTagMode("add")}>Add tags</Button>
                <Button size="small" variant="secondary" onClick={() => setTagMode("remove")}>Remove tags</Button>
              </>
            }
            empty={<EmptyState title={q ? "No customers match your search" : "No customers yet"} body={q ? undefined : "Customers appear here after they order or when you add them."} />}
            mobile={(c) => (
              <div className="flex flex-col gap-y-0.5">
                <div className="flex items-center justify-between gap-x-2">
                  <span className="truncate text-[13px] font-medium">{name(c)}</span>
                  <span className="shrink-0 text-[13px]">{formatMoney(c.amount_spent)}</span>
                </div>
                <div className="flex items-center justify-between gap-x-2 text-[12px] text-ui-fg-subtle">
                  <span className="truncate">{c.location || c.email}</span>
                  <span className="shrink-0">{c.orders_count} order{c.orders_count === 1 ? "" : "s"}</span>
                </div>
              </div>
            )}
            columns={[
              { key: "name", header: "Customer name", cell: (c) => <span className="text-[13px] font-medium">{name(c)}</span> },
              { key: "source", header: "Source", cell: (c) => <SourceBadge source={c.source} /> },
              ...[
                { key: "email", header: "Email", cell: (c: any) => <span className="text-ui-fg-subtle">{c.email}</span> },
                { key: "sub", header: "Email subscription", cell: (c: any) => <Subscribed state={c.email_marketing} /> },
                { key: "loc", header: "Location", cell: (c: any) => c.location ?? "" },
                { key: "phone", header: "Phone", cell: (c: any) => c.phone ?? "" },
                { key: "orders", header: "Orders", cell: (c: any) => `${c.orders_count} order${c.orders_count === 1 ? "" : "s"}` },
                { key: "spent", header: "Amount spent", cell: (c: any) => formatMoney(c.amount_spent) },
                { key: "created", header: "Customer since", cell: (c: any) => (c.created_at ? formatDate(c.created_at) : "") },
                { key: "sms", header: "SMS subscription", cell: (c: any) => <Subscribed state={c.sms_marketing} /> },
                { key: "last", header: "Last order", cell: (c: any) => (c.last_order_at ? formatDate(c.last_order_at, false) : "—") },
                { key: "prev_orders", header: "Prev. store orders", cell: (c: any) => <span className="text-ui-fg-subtle">{prevOrders(c.previous_store)}</span> },
                { key: "prev_spent", header: "Prev. store spent", cell: (c: any) => <span className="text-ui-fg-subtle">{c.previous_store ? formatMoney(c.previous_store.spent) : "—"}</span> },
                { key: "prev_since", header: "Prev. store since", cell: (c: any) => <span className="text-ui-fg-subtle">{c.previous_store?.since ? formatDate(c.previous_store.since, false) : "—"}</span> },
                { key: "prev_last", header: "Prev. store last order", cell: (c: any) => <span className="text-ui-fg-subtle">{c.previous_store?.last_order_at ? formatDate(c.previous_store.last_order_at, false) : c.previous_store?.last_order_name ?? "—"}</span> },
                { key: "prev_email", header: "Prev. store email", cell: (c: any) => (c.previous_store ? <Subscribed state={c.previous_store.email_marketing} /> : <span className="text-ui-fg-subtle">—</span>) },
              ].filter((col) => cols.isOn(col.key)),
            ]}
          />
        )}
      </Section>
      {importing && <CustomerImportDialog onClose={() => setImporting(false)} />}
      {tagMode && (
        <TagsDrawer
          noun="customer"
          mode={tagMode}
          count={selected.size}
          busy={bulk.isPending}
          onClose={() => setTagMode(null)}
          onApply={(tags) => {
            bulk.mutate({ ids: [...selected], action: tagMode === "add" ? "add_tags" : "remove_tags", tags }, { onSuccess: () => (toast.success(tagMode === "add" ? "Tags added" : "Tags removed"), setSelected(new Set())), onError: (e) => toast.error(errorMessage(e)) })
            setTagMode(null)
          }}
        />
      )}
    </>
  )
}

// ── Add customer ───────────────────────────────────────────────────────────

const emptyAddress = (): AddressInput => ({ first_name: "", last_name: "", company: "", address_1: "", address_2: "", city: "", province: "", postal_code: "", country_code: "us", phone: "" })

export function NewCustomerPage() {
  const navigate = useNavigate()
  const create = useCreateCustomer()
  const [c, setC] = useState({ first_name: "", last_name: "", email: "", phone: "", subscribed: false, note: "", tags: "" })
  const [addr, setAddr] = useState<AddressInput>(emptyAddress())
  const dirty = Object.values(c).some((v) => v) || Object.entries(addr).some(([k, v]) => k !== "country_code" && v)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!/^\S+@\S+\.\S+$/.test(c.email.trim())) return toast.error("Enter a valid email.")
    const hasAddress = !!addr.address_1?.trim()
    const problem = hasAddress ? addressProblem(addr as any) : null
    if (problem) return toast.error(problem.message)
    create.mutate(
      {
        first_name: c.first_name.trim() || null,
        last_name: c.last_name.trim() || null,
        email: c.email.trim().toLowerCase(),
        phone: c.phone.trim() || null,
        metadata: {
          email_marketing: c.subscribed ? "subscribed" : "not_subscribed",
          note: c.note.trim() || null,
          tags: c.tags.split(",").map((t) => t.trim()).filter(Boolean),
        },
        address: hasAddress ? { ...trimAddress(addr), first_name: addr.first_name || c.first_name, last_name: addr.last_name || c.last_name } : null,
      },
      { onSuccess: (cu: any) => (toast.success("Customer created"), navigate(`/customers/${cu.id}`, { replace: true })), onError: (err) => toast.error(errorMessage(err)) }
    )
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-[760px] pb-24">
      <PageHeader back="/customers" title="New customer" />
      <div className="flex flex-col gap-4">
        <Section title="Customer overview">
          <div className="grid gap-4 px-4 py-3 sm:grid-cols-2">
            <Field label="First name"><Input value={c.first_name} onChange={(e) => setC({ ...c, first_name: e.target.value })} /></Field>
            <Field label="Last name"><Input value={c.last_name} onChange={(e) => setC({ ...c, last_name: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Email"><Input type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></Field></div>
            <div className="sm:col-span-2"><Field label="Phone number"><Input type="tel" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} /></Field></div>
            <label className="flex items-start gap-x-2 sm:col-span-2">
              <Checkbox className="mt-0.5" checked={c.subscribed} onCheckedChange={(v) => setC({ ...c, subscribed: v === true })} />
              <div>
                <Text size="small">Customer agreed to receive marketing emails.</Text>
                <Text size="xsmall" className="text-ui-fg-subtle">You should ask your customers for permission before you subscribe them to your marketing emails.</Text>
              </div>
            </label>
          </div>
        </Section>
        <Section title="Default address">
          <div className="px-4 py-3">
            <Text size="small" className="mb-3 text-ui-fg-subtle">The primary address of this customer</Text>
            <SharedAddressFields value={addr as any} onChange={(a) => setAddr(a as AddressInput)} />
          </div>
        </Section>
        <Section title="Notes">
          <div className="px-4 py-3">
            <Field label="Note" hint="Add notes about your customer.">
              <Textarea rows={3} value={c.note} maxLength={5000} onChange={(e) => setC({ ...c, note: e.target.value })} />
            </Field>
          </div>
        </Section>
        <Section title="Tags">
          <div className="px-4 py-3">
            <Field label="Tags" hint="Separate tags with commas, e.g. VIP, wholesale">
              <Input value={c.tags} onChange={(e) => setC({ ...c, tags: e.target.value })} />
            </Field>
          </div>
        </Section>
      </div>
      <SaveBar visible={dirty} message="Unsaved customer" saving={create.isPending} onDiscard={() => navigate("/customers")} />
    </form>
  )
}

const trimAddress = (a: AddressInput): AddressInput => Object.fromEntries(Object.entries(a).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])) as AddressInput


// ── Customer page ──────────────────────────────────────────────────────────

export function CustomerDetailPage() {
  const { id = "" } = useParams()
  const { data, error } = useCustomerDetail(id)
  const summary = useCustomerSummary(id)
  const can = useCan()
  const orderName = useOrderName()
  const navigate = useNavigate()
  const del = useDeleteCustomer(id)
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [erasing, setErasing] = useState(false)
  const c: any = data?.customer
  if (error) return <ErrorState error={error} />
  if (!c) return null
  const canWrite = can("customers:write")
  const s = summary.data
  const meta = (c.metadata ?? {}) as Record<string, any>
  const last = s?.recent_orders[0]

  return (
    <div className="pb-24">
      <PageHeader
        back="/customers"
        title={fullName(c) || c.email}
        subtitle={`Customer for ${relativeTime(c.created_at).replace(" ago", "")}`}
        actions={
          (canWrite || can("draft_orders:write") || can("customer_privacy:read")) && (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="small" variant="secondary">More actions <EllipsisHorizontal /></Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {can("draft_orders:write") && <DropdownMenu.Item onClick={() => navigate(`/orders/drafts/new?customer=${c.id}`)}>Create order</DropdownMenu.Item>}
                {canWrite && <DropdownMenu.Item onClick={() => setEditing(true)}>Edit contact information</DropdownMenu.Item>}
                {can("customer_privacy:read") && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item onClick={() => downloadCustomerData(c.id).catch((e) => toast.error(errorMessage(e)))}>Request customer data</DropdownMenu.Item>
                    {can("customer_privacy:write") && <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setErasing(true)}>Erase personal data</DropdownMenu.Item>}
                  </>
                )}
                {canWrite && (
                  <DropdownMenu.Item className="text-ui-fg-error" disabled={(s?.orders_count ?? 0) > 0} onClick={() => setConfirmDelete(true)}>
                    Delete customer{(s?.orders_count ?? 0) > 0 ? " (has orders)" : ""}
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Section>
            <div className="grid grid-cols-3 divide-x">
              {[
                ["Amount spent", formatMoney(s?.amount_spent ?? 0)],
                ["Orders", String(s?.orders_count ?? 0)],
                ["Customer since", formatDate(c.created_at, false)],
              ].map(([k, v]) => (
                <div key={k} className="px-4 py-3">
                  <Text size="xsmall" className="text-ui-fg-subtle">{k}</Text>
                  <Text size="small" weight="plus" className="mt-0.5">{v}</Text>
                </div>
              ))}
            </div>
          </Section>
          {meta.previous_store && <PreviousStoreCard p={meta.previous_store as PreviousStore} here={{ spent: s?.amount_spent ?? 0, orders: s?.orders_count ?? 0, since: c.created_at, last: s?.last_order_at ?? null }} />}

          <Section title={last ? "Last order placed" : "Orders"}>
            {last ? (
              <>
                <div className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/orders/${last.id}`} className="text-[13px] font-medium text-ui-fg-interactive hover:underline">{orderName(last.display_id)}</Link>
                      <KindBadge kind={last.kind} />
                      <Status value={last.payment_status} />
                      <Status value={last.status === "canceled" ? "canceled" : last.fulfillment_status} />
                    </div>
                    <Text size="small">{formatMoney(last.total, last.currency_code)}</Text>
                  </div>
                  <Text size="xsmall" className="text-ui-fg-subtle">{formatDate(last.created_at)} from {CHANNEL_LABEL[last.channel ?? "online_store"]}</Text>
                </div>
                <ul className="divide-y">
                  {last.items.map((i) => (
                    <li key={i.id} className="flex items-center gap-x-3 px-4 py-2">
                      <Thumb src={i.thumbnail} size={36} />
                      <Text size="small" className="flex-1 truncate">{i.title}</Text>
                      <Text size="small" className="text-ui-fg-subtle">× {i.quantity}</Text>
                    </li>
                  ))}
                </ul>
                {s && s.recent_orders.length > 1 && (
                  <ul className="divide-y">
                    {s.recent_orders.slice(1).map((o) => (
                      <li key={o.id} className="flex items-center justify-between gap-x-3 px-4 py-2">
                        <span className="flex items-center gap-2">
                          <Link to={`/orders/${o.id}`} className="text-[13px] font-medium text-ui-fg-interactive hover:underline">{orderName(o.display_id)}</Link>
                          <KindBadge kind={o.kind} />
                        </span>
                        <Text size="xsmall" className="text-ui-fg-subtle">{formatDate(o.created_at, false)}</Text>
                        <Text size="small">{formatMoney(o.total, o.currency_code)}</Text>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex justify-end px-4 py-3">
                  <Button size="small" variant="secondary" asChild>
                    <Link to={`/orders?q=${encodeURIComponent(c.email)}`}>View all orders</Link>
                  </Button>
                </div>
              </>
            ) : (
              <EmptyState title="This customer hasn't placed any orders yet" />
            )}
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Section title="Customer" actions={canWrite && <IconButton size="small" variant="transparent" aria-label="Edit customer" onClick={() => setEditing(true)}><PencilSquare /></IconButton>}>
            <div className="flex flex-col gap-y-1 px-4 py-3">
              <Text size="small" weight="plus">Contact information</Text>
              <Text size="small" className="break-all text-ui-fg-interactive">{c.email}</Text>
              <Text size="small" className="text-ui-fg-subtle">{c.phone || "No phone number"}</Text>
              <Text size="xsmall" className="text-ui-fg-muted">{c.has_account ? "Has a customer account" : "No customer account"}</Text>
            </div>
            <div className="flex flex-col gap-y-1.5 px-4 py-3">
              <Text size="small" weight="plus">Marketing</Text>
              <div className="flex items-center justify-between gap-2"><Text size="small" className="text-ui-fg-subtle">Email</Text><Subscribed state={meta.email_marketing} /></div>
              <div className="flex items-center justify-between gap-2"><Text size="small" className="text-ui-fg-subtle">SMS</Text><Subscribed state={meta.sms_opt_out === true ? "unsubscribed" : meta.sms_marketing} /></div>
              {(meta.email_marketing_source === "previous_store" || meta.sms_marketing_source === "previous_store") && <Text size="xsmall" className="text-ui-fg-muted">Consent carried over from the previous store</Text>}
            </div>
          </Section>
          <AddressesCard customer={c} canWrite={canWrite} />
          <TagsNotesCard customer={c} canWrite={canWrite} />
        </div>
      </div>

      {editing && <ContactDrawer customer={c} onClose={() => setEditing(false)} />}
      {erasing && <EraseDialog customer={c} onClose={() => setErasing(false)} />}
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {fullName(c) || c.email}?</Prompt.Title>
            <Prompt.Description>This customer and their saved addresses are removed. This can't be undone.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate(undefined, { onSuccess: () => (toast.success("Customer deleted"), navigate("/customers", { replace: true })), onError: (e) => toast.error(errorMessage(e)) })}>
              Delete customer
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}

function ContactDrawer({ customer, onClose }: { customer: any; onClose: () => void }) {
  const update = useUpdateCustomer(customer.id)
  const meta = customer.metadata ?? {}
  const [v, setV] = useState({
    first_name: customer.first_name ?? "",
    last_name: customer.last_name ?? "",
    email: customer.email ?? "",
    phone: customer.phone ?? "",
    subscribed: meta.email_marketing === "subscribed" || meta.email_marketing === "SUBSCRIBED",
  })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>Edit customer</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name"><Input value={v.first_name} onChange={(e) => setV({ ...v, first_name: e.target.value })} /></Field>
            <Field label="Last name"><Input value={v.last_name} onChange={(e) => setV({ ...v, last_name: e.target.value })} /></Field>
          </div>
          <Field label="Email"><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
          <Field label="Phone number"><Input type="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
          <label className="flex items-center gap-x-2">
            <Checkbox checked={v.subscribed} onCheckedChange={(c) => setV({ ...v, subscribed: c === true })} />
            <Text size="small">Customer agreed to receive marketing emails</Text>
          </label>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button
            size="small"
            isLoading={update.isPending}
            onClick={() => {
              if (!/^\S+@\S+\.\S+$/.test(v.email.trim())) return toast.error("Enter a valid email.")
              update.mutate(
                {
                  first_name: v.first_name.trim() || null,
                  last_name: v.last_name.trim() || null,
                  email: v.email.trim().toLowerCase(),
                  phone: v.phone.trim() || null,
                  metadata: { ...meta, email_marketing: v.subscribed ? "subscribed" : "not_subscribed" },
                },
                { onSuccess: () => (toast.success("Customer saved"), onClose()), onError: (e) => toast.error(errorMessage(e)) }
              )
            }}
          >
            Save
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

function AddressesCard({ customer, canWrite }: { customer: any; canWrite: boolean }) {
  const save = useSaveAddress(customer.id)
  const del = useDeleteAddress(customer.id)
  const [editing, setEditing] = useState<{ id?: string; a: AddressInput } | null>(null)
  const addresses: any[] = customer.addresses ?? []
  const sorted = [...addresses].sort((a, b) => Number(b.is_default_shipping) - Number(a.is_default_shipping))
  return (
    <Section title="Addresses" actions={canWrite && <Button size="small" variant="transparent" onClick={() => setEditing({ a: { ...emptyAddress(), first_name: customer.first_name ?? "", last_name: customer.last_name ?? "" } })}>Add</Button>}>
      {sorted.length ? (
        sorted.map((a) => (
          <div key={a.id} className="flex items-start justify-between gap-x-2 px-4 py-3">
            <div>
              {a.is_default_shipping && <Text size="xsmall" weight="plus" className="mb-1 text-ui-fg-subtle">Default address</Text>}
              <AddressText address={a} />
            </div>
            {canWrite && (
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <IconButton size="small" variant="transparent" aria-label="Address actions"><EllipsisHorizontal /></IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onClick={() => setEditing({ id: a.id, a: { first_name: a.first_name ?? "", last_name: a.last_name ?? "", company: a.company ?? "", address_1: a.address_1 ?? "", address_2: a.address_2 ?? "", city: a.city ?? "", province: a.province ?? "", postal_code: a.postal_code ?? "", country_code: a.country_code ?? "us", phone: a.phone ?? "" } })}>
                    Edit address
                  </DropdownMenu.Item>
                  {!a.is_default_shipping && (
                    <DropdownMenu.Item onClick={() => save.mutate({ id: a.id, address: { is_default_shipping: true, is_default_billing: true } }, { onSuccess: () => toast.success("Default address updated"), onError: (e) => toast.error(errorMessage(e)) })}>
                      Set as default
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item className="text-ui-fg-error" onClick={() => del.mutate(a.id, { onSuccess: () => toast.success("Address deleted"), onError: (e) => toast.error(errorMessage(e)) })}>
                    <Trash /> Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            )}
          </div>
        ))
      ) : (
        <Text size="small" className="px-4 py-3 text-ui-fg-muted">No addresses</Text>
      )}
      {editing && (
        <Drawer open onOpenChange={(o) => !o && setEditing(null)}>
          <Drawer.Content>
            <Drawer.Header><Drawer.Title>{editing.id ? "Edit address" : "Add address"}</Drawer.Title></Drawer.Header>
            <Drawer.Body className="overflow-y-auto">
              <SharedAddressFields value={editing.a as any} onChange={(a) => setEditing({ ...editing, a: a as AddressInput })} />
            </Drawer.Body>
            <Drawer.Footer>
              <Button variant="secondary" size="small" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                size="small"
                isLoading={save.isPending}
                onClick={() => {
                  if (!editing.a.address_1?.trim() || !editing.a.city?.trim()) return toast.error("Enter the street address and city.")
                  const problem = addressProblem(editing.a as any)
                  if (problem) return toast.error(problem.message)
                  save.mutate(
                    { id: editing.id, address: { ...trimAddress(editing.a), ...(addresses.length === 0 ? { is_default_shipping: true, is_default_billing: true } : {}) } },
                    { onSuccess: () => (toast.success("Address saved"), setEditing(null)), onError: (e) => toast.error(errorMessage(e)) }
                  )
                }}
              >
                Save
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer>
      )}
    </Section>
  )
}

function TagsNotesCard({ customer, canWrite }: { customer: any; canWrite: boolean }) {
  const update = useUpdateCustomer(customer.id)
  const meta = customer.metadata ?? {}
  const [note, setNote] = useState<string>(meta.note ?? "")
  const [tags, setTags] = useState<string[]>(Array.isArray(meta.tags) ? meta.tags : [])
  const [text, setText] = useState("")
  const dirty = note !== (meta.note ?? "") || JSON.stringify(tags) !== JSON.stringify(Array.isArray(meta.tags) ? meta.tags : [])
  // Accepts one tag or a pasted "a, b, c" list; de-duplicates case-insensitively.
  const addTags = (raw: string) => {
    const incoming = raw.split(",").map((t) => t.trim().slice(0, 40)).filter(Boolean)
    if (!incoming.length) return
    const next = [...tags]
    for (const t of incoming) if (!next.some((x) => x.toLowerCase() === t.toLowerCase())) next.push(t)
    setTags(next)
    setText("")
  }
  return (
    <>
      <Section title="Tags">
        <div className="flex flex-col gap-3 px-4 py-3">
          {canWrite && (
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  size="small"
                  value={text}
                  aria-label="Find or create tags"
                  placeholder="Find or create tags"
                  onChange={(e) => setText(e.target.value)}
                  onBlur={() => addTags(text)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault()
                      addTags(text)
                    } else if (e.key === "Backspace" && !text && tags.length) {
                      setTags(tags.slice(0, -1))
                    }
                  }}
                />
              </div>
              <Button type="button" size="small" variant="secondary" disabled={!text.trim()} onMouseDown={(e) => e.preventDefault()} onClick={() => addTags(text)}>
                Add
              </Button>
            </div>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5" role="list" aria-label="Customer tags">
              {tags.map((t) => (
                <span key={t} role="listitem" className="inline-flex max-w-full items-center gap-1 rounded-md bg-ui-bg-component py-0.5 pl-2 pr-1 text-[13px] text-ui-fg-base">
                  <span className="truncate">{t}</span>
                  {canWrite && (
                    <button
                      type="button"
                      aria-label={`Remove ${t}`}
                      className="flex h-5 w-5 items-center justify-center rounded text-ui-fg-muted hover:bg-ui-bg-component-hover hover:text-ui-fg-base"
                      onClick={() => setTags(tags.filter((x) => x !== t))}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {!tags.length && !canWrite && <Text size="small" className="text-ui-fg-muted">No tags</Text>}
        </div>
      </Section>
      <Section title="Notes">
        <div className="px-4 py-3">
          {canWrite ? (
            <Textarea rows={3} value={note} maxLength={5000} placeholder="Add a note" onChange={(e) => setNote(e.target.value)} />
          ) : (
            <Text size="small" className={meta.note ? "whitespace-pre-wrap" : "text-ui-fg-muted"}>{meta.note || "No notes"}</Text>
          )}
        </div>
      </Section>
      {canWrite && (
        <SaveBar
          visible={dirty}
          message="Unsaved changes"
          saving={update.isPending}
          onDiscard={() => (setNote(meta.note ?? ""), setTags(Array.isArray(meta.tags) ? meta.tags : []))}
          onSave={() => update.mutate({ metadata: { ...meta, note: note.trim() || null, tags } }, { onSuccess: () => toast.success("Customer saved"), onError: (e) => toast.error(errorMessage(e)) })}
        />
      )}
    </>
  )
}

/** Request customer data: everything stored about the customer as a JSON download. */
async function downloadCustomerData(id: string) {
  const data = await sdk.client.fetch(`/admin/terify/customers/${id}/privacy`)
  const a = document.createElement("a")
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }))
  a.download = `customer-data-${id}.json`
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  toast.success("Customer data downloaded")
}

function EraseDialog({ customer, onClose }: { customer: any; onClose: () => void }) {
  const qc = useQueryClient()
  const [typed, setTyped] = useState("")
  const erase = useMutation({
    mutationFn: () => sdk.client.fetch<{ orders_redacted: number }>(`/admin/terify/customers/${customer.id}/privacy`, { method: "POST", body: { confirm: "ERASE" } }),
    onSuccess: (r) => {
      toast.success(`Personal data erased${r.orders_redacted ? `; ${r.orders_redacted} order${r.orders_redacted === 1 ? "" : "s"} redacted` : ""}`)
      qc.invalidateQueries({ queryKey: ["customer", customer.id] })
      qc.invalidateQueries({ queryKey: ["customers"] })
      onClose()
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <Prompt open onOpenChange={(o) => !o && onClose()}>
      <Prompt.Content>
        <Prompt.Header>
          <Prompt.Title>Erase personal data for {fullName(customer) || customer.email}?</Prompt.Title>
          <Prompt.Description>
            Removes their name, email, phone, addresses, notes and tags, and redacts names and addresses on their orders. Order amounts and items stay for your records. This can't be undone.
          </Prompt.Description>
        </Prompt.Header>
        <div className="px-6 pb-4">
          <Input aria-label="Type ERASE to confirm" placeholder="Type ERASE to confirm" value={typed} onChange={(e) => setTyped(e.target.value)} />
        </div>
        <Prompt.Footer>
          <Prompt.Cancel onClick={onClose}>Cancel</Prompt.Cancel>
          <Button size="small" variant="danger" disabled={typed !== "ERASE"} isLoading={erase.isPending} onClick={() => erase.mutate()}>Erase personal data</Button>
        </Prompt.Footer>
      </Prompt.Content>
    </Prompt>
  )
}

/** This store and the previous store side by side (customer page). */
function PreviousStoreCard({ p, here }: { p: PreviousStore; here: { spent: number; orders: number; since: string; last: string | null } }) {
  const rows: [string, string, string][] = [
    ["Amount spent", formatMoney(here.spent), formatMoney(p.spent, p.currency)],
    ["Orders", String(here.orders), String(p.orders)],
    ["Customer since", formatDate(here.since, false), p.since ? formatDate(p.since, false) : "—"],
    ["Last order", here.last ? formatDate(here.last, false) : "—", p.last_order_at ? formatDate(p.last_order_at, false) : p.last_order_name ?? "—"],
  ]
  const total = { spent: here.spent + (p.currency === "USD" ? p.spent : 0), orders: here.orders + p.orders }
  return (
    <Section title="This store and the previous store">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-ui-fg-subtle">
              <th className="px-4 py-2 font-normal" />
              <th className="px-4 py-2 font-medium">Here</th>
              <th className="px-4 py-2 font-medium">Previous store</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map(([k, a, b]) => (
              <tr key={k}>
                <td className="px-4 py-2 text-ui-fg-subtle">{k}</td>
                <td className="px-4 py-2">{a}</td>
                <td className="px-4 py-2">{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2">
        <Text size="xsmall" className="text-ui-fg-subtle">Lifetime: {formatMoney(total.spent)} over {total.orders} order{total.orders === 1 ? "" : "s"}</Text>
        <Text size="xsmall" className="text-ui-fg-muted">{p.tags.length ? `Tags there: ${p.tags.slice(0, 4).join(", ")}${p.tags.length > 4 ? "…" : ""} · ` : ""}Updated {formatDate(p.synced_at, false)}</Text>
      </div>
    </Section>
  )
}
